import {
  Type,
  ButtonSecondary,
  Table,
  TableRow,
  TableCell,
  TableCheckbox,
  TableHeaderCell,
  spacer8,
  spacer16,
  spacer24,
} from '@spotify-internal/encore-web';
import { useReducer } from 'react';
import styled from 'styled-components';
import { MissingRecording, AlterErrorReportUpdateAction } from '../../../ErrorReporting/types';
import recordingReducer, {
  getRecordingReducerState,
  TOGGLE_ONE,
  TOGGLE_ALL,
  UNSELECT_ALL,
  RecordingsState,
} from '../recordingReducer';
import { isResolveButtonEnabled, isUnresolveButtonEnabled } from '../recordingTableActionsHelper';
import gTagManager from '../../../../libs/services/logging';
import { useT } from '@spotify-internal/i18n-core';
import { Link } from 'react-router-dom';

const Image = styled.img`
  height: 36px;
  margin: ${spacer8};
`;

type MissingRecordingsProps = {
  missingRecordings: MissingRecording[] | undefined;
  onUpdateMissingRecordings: (
    operation: AlterErrorReportUpdateAction,
    recordingGids: RecordingsState,
  ) => Promise<void>;
  songwriterOrgUri: string;
  songwriterDisplayName: string;
};

const MissingRecordings = ({
  missingRecordings,
  onUpdateMissingRecordings,
  songwriterOrgUri,
  songwriterDisplayName,
}: MissingRecordingsProps) => {
  const t = useT();
  const [state, dispatch] = useReducer(
    recordingReducer,
    getRecordingReducerState(missingRecordings),
  );

  if (!missingRecordings || missingRecordings.length === 0) {
    return null;
  }

  const toggleRecording = (missingRecording: MissingRecording) => {
    if (isRecordingUnselected(missingRecording)) {
      fireSelectRecordingEvent(missingRecording);
    } else {
      fireUnselectRecordingEvent(missingRecording);
    }
    dispatch({
      type: TOGGLE_ONE,
      recordingGid: missingRecording.recordingGid,
    });
  };

  const toggleAllRecordings = () => {
    if (state.allRecordingsChecked) {
      missingRecordings.forEach(fireUnselectRecordingEvent);
    } else {
      missingRecordings.filter(isRecordingUnselected).forEach(fireSelectRecordingEvent);
    }
    dispatch({
      type: TOGGLE_ALL,
    });
  };

  const unselectAllRecordings = () => {
    dispatch({
      type: UNSELECT_ALL,
    });
  };

  const isRecordingUnselected = (missingRecording: MissingRecording) => {
    return !state.recordings[missingRecording.recordingGid];
  };

  const isRecordingSelected = (missingRecording: MissingRecording) => {
    return state.recordings[missingRecording.recordingGid];
  };

  function fireSelectRecordingEvent(missingRecording: MissingRecording) {
    gTagManager({
      event: 'selectMissingRecording',
      missingRecording: `${missingRecording.recordingName} by ${missingRecording.recordingArtistName}`,
      creatorEntityUri: songwriterOrgUri,
      songwriterDisplayName: songwriterDisplayName,
      recordingTitle: missingRecording.recordingName,
    });
  }

  function fireUnselectRecordingEvent(missingRecording: MissingRecording) {
    gTagManager({
      event: 'unselectMissingRecording',
      missingRecording: `${missingRecording.recordingName} by ${missingRecording.recordingArtistName}`,
      creatorEntityUri: songwriterOrgUri,
      songwriterDisplayName: songwriterDisplayName,
      recordingTitle: missingRecording.recordingName,
    });
  }

  function fireResolveRecordingEvent(missingRecording: MissingRecording) {
    gTagManager({
      event: 'resolveMissingRecording',
      missingRecording: `${missingRecording.recordingName} by ${missingRecording.recordingArtistName}`,
      creatorEntityUri: songwriterOrgUri,
      songwriterDisplayName: songwriterDisplayName,
      recordingTitle: missingRecording.recordingName,
    });
  }

  function fireUnresolveRecordingEvent(missingRecording: MissingRecording) {
    gTagManager({
      event: 'unresolveMissingRecording',
      missingRecording: `${missingRecording.recordingName} by ${missingRecording.recordingArtistName}`,
      creatorEntityUri: songwriterOrgUri,
      songwriterDisplayName: songwriterDisplayName,
      recordingTitle: missingRecording.recordingName,
    });
  }

  return (
    <div>
      <div>
        <Type as="h3" variant="celloCanon">
          {t(
            'H_MISSINGREC_0',
            'Add works for these recordings',
            'Header for page to add missing works to recordings',
          )}
        </Type>
        <Type
          as="p"
          variant="mesto"
          semanticColor="textSubdued"
          style={{ marginBlockStart: spacer8, marginBlockEnd: spacer24 }}
        >
          {t(
            'TEXT_MISSINGREC_0',
            'These recordings aren’t matched to works on Spotify for Publishing. To fix this, please go to Works and match the recordings. If works are not in the account, please submit them via CWR.',
            'Header for page to add missing works to recordings',
          )}{' '}
          <Link to="/faq" target="_blank">
            {t(
              'TEXT_MISSINGREC_1',
              'Learn more',
              'Link to the Frequently Asked Questions (FAQ) page to learn more about CWR file submission',
            )}
          </Link>
        </Type>
      </div>
      <div>
        <Table>
          <thead>
            <TableRow>
              <TableHeaderCell>
                <TableCheckbox
                  id="checkAll"
                  onChange={() => toggleAllRecordings()}
                  checked={state.allRecordingsChecked}
                />
              </TableHeaderCell>
              <TableHeaderCell>
                {t('CH_MISSINGREC_0', 'Recording', 'Column header for table of recordings')}
              </TableHeaderCell>
              <TableHeaderCell>
                {t('CH_MISSINGREC_1', 'Status', 'Column header for table of recordings')}
              </TableHeaderCell>
            </TableRow>
          </thead>
          <tbody>
            {missingRecordings.map((missingRecording) => {
              const recordingInfo = `${missingRecording.recordingName} - ${missingRecording.recordingArtistName}`;
              const resolvedStatus = missingRecording.resolved
                ? t('TEXT_MISSINGREC_2', 'Resolved', 'The status of the missing recording')
                : t('TEXT_MISSINGREC_3', 'Unresolved', 'The status of the missing recording');
              const alText = t(
                'ALTTEXT_MISSINGREC_0',
                'Cover art for {recordingName} by {artistName}',
                'Alt text for recording cover art',
                {
                  recordingName: missingRecording.recordingName,
                  artistName: missingRecording.recordingArtistName,
                },
              );

              const recordingGid = missingRecording.recordingGid;
              return (
                <TableRow style={{ height: '52px' }} key={recordingGid}>
                  <TableCell>
                    <TableCheckbox
                      id={recordingGid}
                      onChange={() => toggleRecording(missingRecording)}
                      checked={state.recordings[recordingGid]}
                    />
                  </TableCell>
                  <TableCell style={{ display: 'flex', alignItems: 'center' }}>
                    <Image src={missingRecording.coverArt300} alt={alText} />
                    {recordingInfo}
                  </TableCell>
                  <TableCell>
                    <Type
                      semanticColor={missingRecording.resolved ? 'textPositive' : 'textSubdued'}
                    >
                      {resolvedStatus}
                    </Type>
                  </TableCell>
                </TableRow>
              );
            })}
          </tbody>
        </Table>
      </div>
      <div style={{ marginBlockStart: spacer16, display: 'flex', gap: spacer16 }}>
        <ButtonSecondary
          buttonSize="sm"
          onClick={async () => {
            await onUpdateMissingRecordings('RESOLVE', state.recordings);
            missingRecordings.filter(isRecordingSelected).forEach(fireResolveRecordingEvent);
            unselectAllRecordings();
          }}
          disabled={!isResolveButtonEnabled(state, missingRecordings)}
        >
          {t(
            'BUTTON_MISSINGREC_0',
            'Mark as resolved',
            'Button to mark a missing recording as resolved',
          )}
        </ButtonSecondary>
        <ButtonSecondary
          buttonSize="sm"
          onClick={async () => {
            await onUpdateMissingRecordings('UNRESOLVE', state.recordings);
            missingRecordings.filter(isRecordingSelected).forEach(fireUnresolveRecordingEvent);
            unselectAllRecordings();
          }}
          disabled={!isUnresolveButtonEnabled(state, missingRecordings)}
        >
          {t(
            'BUTTON_MISSINGREC_1',
            'Mark as unresolved',
            'Button to mark a missing recording as unresolved',
          )}
        </ButtonSecondary>
      </div>
    </div>
  );
};

export default MissingRecordings;
